// ============================================================================
// CHANGO V5.0 — Forgot Password Page (T-D1-022)
// ============================================================================
// POST /api/auth/forgot-password {email}
// Always shows the same success message whether the email exists or not, to
// avoid leaking account-existence via the reset-password flow.
// ============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';

const schema = z.object({
  email: z.string().trim().email('请输入有效的邮箱地址')
});

export default function ForgotPassword() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { email: '' }
  });

  const onSubmit = async ({ email }) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      if (err instanceof HttpError && err.status !== 429) {
        // §20 rule: never reveal account existence — only surface rate-limit and network errors.
        setSent(true);
      } else {
        setServerError(err?.message || '请求失败，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold text-slate-800">找回密码</h1>

        {sent ? (
          <>
            <div className="alert-info mt-6">
              如果该邮箱存在，我们已发送一封重置邮件。请检查收件箱（及垃圾邮件文件夹）。
              邮件链接有效期为 1 小时。
            </div>
            <p className="mt-6 text-center text-sm">
              <Link to="/login" className="text-chango-600 hover:underline">返回登录</Link>
            </p>
          </>
        ) : (
          <form noValidate onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
            <p className="text-sm text-slate-600">
              输入您注册时使用的邮箱，我们会将密码重置链接发送过去。
            </p>

            {serverError ? <div role="alert" className="alert-error">{serverError}</div> : null}

            <div>
              <label className="label" htmlFor="email">邮箱</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`field ${errors.email ? 'field-error' : ''}`}
                {...register('email')}
              />
              {errors.email ? <p className="err">{errors.email.message}</p> : null}
            </div>

            <button type="submit" disabled={submitting || !isValid} className="btn-primary w-full">
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '发送中…' : '发送重置链接'}
            </button>

            <p className="text-center text-sm">
              <Link to="/login" className="text-chango-600 hover:underline">返回登录</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
